import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Truck, UserCheck, Home as HomeIcon, Bike, MapPin, Mail, Phone, ExternalLink, Instagram, Facebook } from 'lucide-react';

// TikTok icon component (lucide doesn't have TikTok, so we'll use a custom SVG)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Home = () => {
  const services = [
    {
      id: 1,
      icon: Truck,
      title: 'Alquiler sin conductor',
      description: 'Alquila nuestra furgoneta y conduce tú mismo. Perfecto para mudanzas, transportes o escapadas.',
      features: ['Flexibilidad total', 'Reserva por horas o días', 'Seguro incluido'],
      pricing: {
        model: 'Trafic L1H1',
        rates: [
          '30€ / 4h',
          '43€ / 24h',
          '85€ / 48h'
        ],
        extras: [
          '200 km por días incluido',
          'Km extra 0,24€',
          'Seguro incluido'
        ]
      }
    },
    {
      id: 2,
      icon: UserCheck,
      title: 'Alquiler con conductor',
      description: 'Relájate mientras nuestro conductor profesional se encarga de todo el transporte.',
      features: ['Conductor experimentado', 'Servicio puerta a puerta', 'Sin preocupaciones'],
      pricing: {
        custom: 'Presupuesto a medida para prestaciones de transporte con conductor desde 30€'
      }
    },
    {
      id: 3,
      icon: HomeIcon,
      title: 'Servicio de mudanza',
      description: 'Servicio completo de mudanza con conductor profesional y ayuda en carga/descarga.',
      features: ['Carga y descarga incluida', 'Protección de muebles', 'Servicio rápido y eficaz'],
      pricing: {
        whatsapp: true
      }
    },
    {
      id: 4,
      icon: Bike,
      title: 'Transporte de motos',
      description: 'Transporte seguro de motocicletas a cualquier destino en la región de Alicante.',
      features: ['Sujeción profesional', 'Seguro de transporte', 'Máxima seguridad'],
      pricing: {
        whatsapp: true
      }
    }
  ];

  const images = [
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/nq1m0394_camion.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/sjip7ods_camion2.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/4n23g354_camion3.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/5avhafv9_moto1.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/gdkpa4lz_moto2.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/p431hee0_camion1.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/lg90h4q6_camion4.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/8p0lrs3p_meuble1.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/4gwjqynh_meuble2.JPG',
    'https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/iakw9qvq_moto5.JPG'
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-6">
              <div className="flex items-center gap-2 md:gap-3">
                <Truck className="h-6 w-6 md:h-8 md:w-8 text-sky-500" />
                <h1 className="text-base md:text-2xl font-bold text-gray-900">ALC Furgonetas</h1>
              </div>
              <div className="flex items-center gap-2 md:gap-3">
                {/* Phone - visible on all screens */}
                <a 
                  href="tel:+34628527035" 
                  className="flex items-center gap-1 text-sky-600 hover:text-sky-700 transition-colors"
                >
                  <Phone className="h-8 w-8 md:h-5 md:w-5" />
                  <span className="hidden sm:inline text-sm md:text-base font-semibold">+34 628 527 035</span>
                </a>
                {/* WhatsApp - visible on all screens */}
                <a 
                  href="https://wa.me/34628527035" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-2 py-2 md:px-3 md:py-2 rounded-lg transition-colors"
                  title="Contactar por WhatsApp"
                >
                  <Phone className="h-6 w-6 md:h-4 md:w-4" />
                  <span className="hidden sm:inline text-xs md:text-base">WhatsApp</span>
                </a>
                {/* 7/7 badge - visible on mobile only */}
                <span className="md:hidden bg-sky-500 text-white px-2 py-1 rounded text-xs font-bold">
                  7/7
                </span>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#servicios" className="text-gray-700 hover:text-sky-500 transition-colors">Servicios 7/7</a>
              <a href="#galeria" className="text-gray-700 hover:text-sky-500 transition-colors">Galería</a>
              <a href="#contacto" className="text-gray-700 hover:text-sky-500 transition-colors">Contacto</a>
              <Button 
                className="bg-sky-500 hover:bg-sky-600 text-white"
                onClick={() => window.open('https://es.getaround.com/r/4975907-e6b?utm_campaign=referral-driver-web&utm_content=copy-link&utm_medium=click&utm_source=drivy', '_blank')}
              >
                Reservar ahora
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-[600px] md:min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_van-alquiler-costa/artifacts/v3v0kqd6_FOND1.png" 
            alt="ALC Furgonetas - Mutxamel" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10 pt-12 md:pt-16">
          <div className="max-w-3xl">
            <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Alquiler de furgonetas en Alicante
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Servicio profesional de alquiler de furgonetas con y sin conductor en Mutxamel. 
              Mudanzas, transporte de motos y más. ¡Reserva ahora en Getaround!
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-6 text-lg"
                onClick={() => window.open('https://es.getaround.com/r/4975907-e6b?utm_campaign=referral-driver-web&utm_content=copy-link&utm_medium=click&utm_source=drivy', '_blank')}
              >
                Reservar en Getaround
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-sky-500 text-sky-600 hover:bg-sky-50 px-8 py-6 text-lg"
                onClick={() => document.getElementById('contacto').scrollIntoView({ behavior: 'smooth' })}
              >
                Contactar
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="servicios" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nuestros Servicios</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Ofrecemos soluciones flexibles de transporte adaptadas a tus necesidades
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={service.id}
                  className="hover:shadow-xl transition-all duration-300 border-2 hover:border-sky-500"
                >
                  <CardHeader>
                    <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-sky-500" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                          <div className="w-1.5 h-1.5 bg-sky-500 rounded-full"></div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t">
                      {service.pricing ? (
                        <div className="space-y-3">
                          {service.pricing.model && (
                            <>
                              <p className="font-bold text-sky-600 text-base">{service.pricing.model}</p>
                              <div className="space-y-1">
                                {service.pricing.rates.map((rate, idx) => (
                                  <p key={idx} className="text-sm font-semibold text-gray-800">{rate}</p>
                                ))}
                              </div>
                              <div className="space-y-1 pt-2">
                                {service.pricing.extras.map((extra, idx) => (
                                  <p key={idx} className="text-xs text-gray-600">{extra}</p>
                                ))}
                              </div>
                            </>
                          )}
                          {service.pricing.custom && (
                            <p className="text-sm font-semibold text-sky-600">{service.pricing.custom}</p>
                          )}
                          {service.pricing.whatsapp && (
                            <a 
                              href={`https://wa.me/34628527035?text=Hola,%20me%20gustaría%20solicitar%20un%20presupuesto%20para%20el%20servicio%20de%20${encodeURIComponent(service.title)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors text-sm font-semibold"
                            >
                              <Phone className="h-4 w-4" />
                              Solicitar presupuesto
                            </a>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm font-semibold text-sky-600">Consultar precio</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="galeria" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nuestra Furgoneta</h2>
            <p className="text-xl text-gray-600">Vehículo profesional y bien equipado para tus necesidades</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {images.map((image, idx) => (
              <div 
                key={idx}
                className="relative overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 aspect-square"
              >
                <img 
                  src={image} 
                  alt={`ALC Furgonetas ${idx + 1}`}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">¿Por qué elegir ALC Furgonetas?</h2>
            <p className="text-lg text-gray-700 mb-6">
              Somos un servicio profesional de alquiler de furgonetas ubicado en Mutxamel, Alicante. 
              Ofrecemos soluciones flexibles para particulares y empresas, con o sin conductor.
            </p>
            <div className="mb-8">
              <Button
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white px-8 py-3 text-base"
                onClick={() => window.open('https://es.getaround.com/users/4975907', '_blank')}
              >
                Consulta nuestras reseñas y calificaciones
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              <div className="text-center">
                <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Fácil reserva</h3>
                <p className="text-gray-600">Reserva online con tu móvil en unos minutos</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Flexibilidad</h3>
                <p className="text-gray-600">Con o sin conductor, según tus necesidades</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Servicio local</h3>
                <p className="text-gray-600">Conocemos la región de Alicante</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Contacto : Whatsapp y Teléfono respuestas rápidas</h2>
              <p className="text-xl text-gray-600">Estamos todo tipo de solicitud y adaptamos segun tus necesidades</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Phone className="h-6 w-6 text-sky-500" />
                    Teléfono
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href="tel:+34628527035"
                    className="text-lg text-sky-600 hover:underline"
                  >
                    +34 628 527 035
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Mail className="h-6 w-6 text-sky-500" />
                    Email
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <a 
                    href="mailto:alctransporte2025@gmail.com"
                    className="text-lg text-sky-600 hover:underline"
                  >
                    alctransporte2025@gmail.com
                  </a>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <MapPin className="h-6 w-6 text-sky-500" />
                    Ubicación
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-lg text-gray-700">
                    Mutxamel, Alicante<br />
                    Comunidad Valenciana, España
                  </p>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white px-12 py-6 text-lg"
                onClick={() => window.open('https://es.getaround.com/r/4975907-e6b?utm_campaign=referral-driver-web&utm_content=copy-link&utm_medium=click&utm_source=drivy', '_blank')}
              >
                Reservar ahora en Getaround
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Truck className="h-8 w-8 text-sky-500" />
                <span className="text-xl font-bold">ALC Furgonetas</span>
              </div>
              <p className="text-gray-400">
                Servicio profesional de alquiler de furgonetas en Mutxamel, Alicante
              </p>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contacto</h3>
              <div className="space-y-2 text-gray-400">
                <a href="tel:+34628527035" className="flex items-center gap-2 hover:text-sky-500 transition-colors">
                  <Phone className="h-4 w-4" />
                  +34 628 527 035
                </a>
                <a href="mailto:alctransporte2025@gmail.com" className="flex items-center gap-2 hover:text-sky-500 transition-colors">
                  <Mail className="h-4 w-4" />
                  alctransporte2025@gmail.com
                </a>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Mutxamel, Alicante
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Síguenos</h3>
              <div className="flex gap-4">
                <a 
                  href="https://www.instagram.com/alcfurgonetas" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-500 transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.facebook.com/alcfurgonetas" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-500 transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </a>
                <a 
                  href="https://www.tiktok.com/@alcfurgonetas" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-sky-500 transition-colors"
                  aria-label="TikTok"
                >
                  <TikTokIcon className="h-5 w-5" />
                </a>
                <a 
                  href="https://wa.me/34628527035" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                  aria-label="WhatsApp"
                >
                  <Phone className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>© 2025 ALC Furgonetas. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
